import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Settings.css";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { getUserDetails, updateEmailPreference, deleteAccount } from "../../Utils/functions";

function Settings() {
    const { user, signOut } = useAuthenticator((context) => [context.user, context.signOut]);
    const navigate = useNavigate();
    const [emailOptOut, setEmailOptOut] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [message, setMessage] = useState({ text: "", type: "" });

    const loadEmailPreference = async (isCancelled) => {
        try {
            const data = await getUserDetails();

            if (!isCancelled() && data?.user != null) {
                setEmailOptOut(Boolean(data?.user?.emailUnsubscribed));
            }
        } catch {
            if (!isCancelled()) setMessage({ text: "Could not load email preference.", type: "error" });
        } finally {
            if (!isCancelled()) setLoading(false);
        }
    };

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        setLoading(true);
        loadEmailPreference(() => cancelled);
        return () => { cancelled = true; };
    }, [user]);

    const handleStopEmails = () => {
        setConfirmAction("stop");
        setShowConfirm(true);
    };

    const handleReceiveEmails = () => {
        setConfirmAction("receive");
        setShowConfirm(true);
    };

    const handleDeleteAccount = () => {
        setConfirmAction("delete");
        setShowConfirm(true);
    };

    const handleConfirmAction = async () => {
        try {
            if (confirmAction === "delete") {
                await deleteAccount();
                signOut();
                navigate("/");
                return;
            }
            if (confirmAction === "stop") {
                await updateEmailPreference();
                setEmailOptOut(true);
                setMessage({ text: "You will no longer receive emails from us.", type: "success" });
            } else {
                await updateEmailPreference();
                setEmailOptOut(false);
                setMessage({ text: "You will receive emails from us again.", type: "success" });
            }
            setShowConfirm(false);
            setConfirmAction(null);
        } catch (err) {
            setMessage({
                text: confirmAction === "delete" ? err.message || "Failed to delete account." : err.message || "Failed to update email preference.",
                type: "error",
            });
            setShowConfirm(false);
            setConfirmAction(null);
        }
    };

    const handleCancelConfirm = () => {
        setShowConfirm(false);
        setConfirmAction(null);
    };

    const logo = (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="92"
            height="111"
            viewBox="0 0 92 111"
            fill="none"
        >
            <path
                d="M65.4449 25.611L75.117 31.0505L50.6292 46.851V58.7662L91.9986 34.7804V27.3284L45.6862 0L0 27.9583V41.6158L45.9926 14.2874L54.946 19.3945L0 53.0676V66.5369L65.4449 25.611Z"
                fill="#00151F"
            />
            <path
                d="M15.0243 80.8149L41.3708 63.7193L41.3708 51.8042L0.00140662 78.3801L0.00140597 85.8322L46.3138 110.57L92 82.612L92 68.9545L92 47.9188L82.8886 53.2288L82.8886 73.8212L46.0074 96.2829L15.0243 80.8149Z"
                fill="#50A854"
            />
        </svg>
    );

    return (
        <div className="settings-wrapper">
            <div className="settings-container">
                <div className="settings-left-section">
                    <h1 className="settings-main-heading">Settings</h1>
                    <div className="settings-divider-line" />
                </div>
                <div className="settings-right-section">
                    <div className="settings-logo">{logo}</div>
                    <div className="settings-content-wrapper">
                        <section className="settings-section">
                            <h2 className="settings-section-heading">Email preferences</h2>
                            {!loading && !emailOptOut && (
                                <p className="settings-section-description">
                                    You can choose to stop receiving all emails from MedRPT, including notifications and updates.
                                </p>
                            )}
                            {message.text && (
                                <p className={`settings-message settings-message--${message.type}`}>
                                    {message.text}
                                </p>
                            )}
                            {loading ? (
                                <p className="settings-status">Loading...</p>
                            ) : emailOptOut ? (
                                <button
                                    type="button"
                                    className="settings-button settings-button--secondary"
                                    onClick={handleReceiveEmails}
                                >
                                    Receive emails
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="settings-button settings-button--secondary"
                                    onClick={handleStopEmails}
                                >
                                    Stop receiving emails
                                </button>
                            )}
                        </section>

                        <section className="settings-section settings-section--danger">
                            <h2 className="settings-section-heading">Account deletion</h2>
                            <p className="settings-section-description">
                                Permanently delete your account and all associated data. This action cannot be undone.
                            </p>
                            <button
                                type="button"
                                className="settings-button settings-button--danger"
                                onClick={handleDeleteAccount}
                            >
                                Delete account
                            </button>
                        </section>

                        {showConfirm && (
                            <div className="settings-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="settings-confirm-title">
                                <div className="settings-confirm-card">
                                    <h3 id="settings-confirm-title" className="settings-confirm-title">
                                        {confirmAction === "delete"
                                            ? "Delete your account?"
                                            : confirmAction === "stop"
                                                ? "Stop receiving emails?"
                                                : "Receive emails?"}
                                    </h3>
                                    <p className="settings-confirm-text">
                                        {confirmAction === "delete"
                                            ? "Your account and all associated data will be permanently deleted. This action cannot be undone."
                                            : confirmAction === "stop"
                                                ? "You will no longer receive any emails from MedRPT. You can change this later from this page."
                                                : "You will start receiving emails from MedRPT again. You can change this later from this page."}
                                    </p>
                                    {confirmAction === "stop" && (
                                        <p className="settings-confirm-note">
                                            N.B. If you click "Yes, stop emails", you will no longer receive any scheduled emailed updates for any question or concern you have submitted.
                                        </p>
                                    )}
                                    <div className="settings-confirm-actions">
                                        <button
                                            type="button"
                                            className="settings-button settings-button--secondary"
                                            onClick={handleCancelConfirm}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className={`settings-button ${confirmAction === "delete" ? "settings-button--danger-confirm" : "settings-button--primary"}`}
                                            onClick={handleConfirmAction}
                                        >
                                            {confirmAction === "delete"
                                                ? "Yes, delete my account"
                                                : confirmAction === "stop"
                                                    ? "Yes, stop emails"
                                                    : "Yes, receive emails"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Settings;
